import { isEmptyHTML } from '@/app/util/editor/editor';
import { LANGUAGES } from '@/app/data/model_constants';
import { FT } from '@/app/data/flipper_constants';

const has_value = (value) => Boolean(value) && !isEmptyHTML(value);

const getters = {
  translationOrFallbackFor:
    (_state, { current_client, current_profile, feature_toggles }) =>
    // eslint-disable-next-line complexity
    (model, field, language = null, { feature_toggle = null } = {}) => {
      if (_.isNil(model) || _.isNil(field)) return '';

      const toggles_enabled = _.flatten(_.valuesIn(feature_toggles ?? {}));
      const is_multilingual =
        toggles_enabled.includes(FT.MULTILINGUAL) ||
        (Boolean(feature_toggle) && toggles_enabled.includes(feature_toggle));
      const default_language = is_multilingual
        ? current_profile.attributes.language
        : current_client.attributes.language;
      const language_requested = language ?? default_language;
      const company_languages = current_client.attributes.languages ?? LANGUAGES;

      if (company_languages.includes(language_requested)) {
        const field_requested = _.get(model.attributes.translations, `${language_requested}.${field}`);
        if (has_value(field_requested)) {
          return { fallback_translation: field_requested };
        }
      }

      const language_client = _.get(model.attributes.translations, `${current_client.attributes.language}.${field}`);
      if (has_value(language_client)) {
        return { fallback_translation: language_client, fallback_language: current_client.attributes.language };
      }

      const remaining_locales = _.filter(
        company_languages,
        (lang) => ![language_requested, current_client.attributes.language].includes(lang),
      );

      let fallback_translation = null;
      let fallback_language = null;
      let index = 0;
      while (_.isNil(fallback_translation) && index < remaining_locales.length) {
        const locale = remaining_locales[index];
        const field_translation = _.get(model.attributes.translations, `${locale}.${field}`);
        if (has_value(field_translation)) {
          fallback_translation = field_translation;
          fallback_language = locale;
        } else {
          index += 1;
        }
      }
      if (_.isNil(fallback_translation) && !language) {
        const field_direct = model.attributes[field];
        if (has_value(field_direct)) {
          return { fallback_translation: field_direct };
        }
      }
      return { fallback_translation, fallback_language };
    },

  // Options are index-locked across locales, so each blank label falls back
  // per index along the same locale chain as translationOrFallbackFor.
  fixedOptionsWithFallbackFor:
    (_state, { current_client, current_profile, feature_toggles }) =>
    (model, language = null) => {
      if (_.isNil(model)) return [];

      const is_multilingual = _.flatten(_.valuesIn(feature_toggles ?? {})).includes(FT.MULTILINGUAL);
      const default_language = is_multilingual
        ? current_profile.attributes.language
        : current_client.attributes.language;
      const language_requested = language ?? default_language;
      const company_languages = current_client.attributes.languages ?? LANGUAGES;

      const locales_ordered = _.uniq([
        ...(company_languages.includes(language_requested) ? [language_requested] : []),
        current_client.attributes.language,
        ...company_languages,
      ]);
      const option_lists = _.filter(
        locales_ordered.map((locale) => _.get(model.attributes.translations, `${locale}.fixed_options`)),
        _.isArray,
      );
      if (option_lists.length === 0) return model.attributes.fixed_options || [];

      const count_options = _.max(_.map(option_lists, 'length')) || 0;
      return _.times(count_options, (index) => {
        const list_filled = option_lists.find((list) => String(list[index] || '').trim());
        return list_filled ? list_filled[index] : '';
      });
    },
};

export default {
  getters,
};
